import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { differenceInCalendarDays } from 'date-fns'

import { ICONS, IconCheck, IconDocument } from './icons'
import { buildCompanyProgress, buildMiniCompanyProgress } from './steps'

import { hasChecklistCounterpart } from '../../shared/companyStepLink'

import styles from './style.module.scss'
import { formatVienna } from '../../shared/datetime'

/*
 * Wo steht diese Junior Company im Programm?
 *
 * Ein Balken mit vier Abschnitten (Anmeldung, Genehmigung,
 * Geschäftstätigkeiten, Berichte) und einem Knoten je Schritt.
 *
 * Der Vorgänger (UI/ProgresBarPage samt UI/ProgressBar und dessen Step) setzte
 * jeden Knoten absolut auf eine feste Prozentzahl und legte die
 * Abschnittsbreiten als 24/26/24/26 % daneben. Beides musste von Hand
 * zusammenpassen; sobald ein Schritt wegfiel (JBC verkauft keine Anteile) oder
 * die Beschriftungen länger wurden, standen Knoten und Überschrift nicht mehr
 * übereinander und die Beschriftungen überlappten. Die Haken kamen als
 * Bilddatei (`Stroke.svg`) — ein `<img>` erbt keine Farbe, erledigt und offen
 * mussten deshalb zwei verschiedene Bauteile sein.
 *
 * Hier bestimmt die Anzahl der Schritte die Breite: Der Balken ist eine Reihe
 * gleich breiter Zellen, die Abschnittsüberschriften wachsen mit der Zahl
 * ihrer Schritte (`flex-grow`), die Farbflächen dahinter ebenso. Ein Schritt
 * mehr oder weniger rückt alles von selbst zurecht.
 *
 * Gezeichnet wird mit SVG im Quelltext (siehe icons.jsx) — die Sinnbilder
 * erben ihre Farbe vom Zustand des Schritts.
 *
 * Dieselbe Ansicht sehen Schüler:innen (Dashboard), die Lehrkraft (Dashboard,
 * für die ausgewählte betreute Company) und die Verwaltung (Firmenprofil).
 *
 * Auf den Dashboards steht darunter dieselbe Sache noch einmal als Checkliste.
 * Welcher Knoten zu welcher Zeile gehört, war nur zu erraten — deshalb hebt
 * ein Klick auf einen Knoten die zugehörigen Zeilen hervor und umgekehrt
 * (`focusId`/`onFocusStep`, Zuordnung in shared/companyStepLink). Ohne diese
 * beiden Angaben — etwa im Firmenprofil der Verwaltung, wo es keine Checkliste
 * gibt — bleibt der Balken wie bisher reine Anzeige.
 */

// Wie in views/common/CompanySteps. Sobald „Alle Zeiten sind Wiener Zeit"
// (PR #91) eingespielt ist, gehört hier `formatVienna` aus shared/datetime hin
// — sonst steht an einer Frist die Zeitzone des Browsers.
const fmt = wert => (wert ? formatVienna(wert, 'dd.MM.yyyy') : '')

function CompanyProgress(props) {
	const { t, i18n } = useTranslation()

	/*
	 * Die Sprache steht am Element, damit der Browser deutsche Wörter richtig
	 * trennt (`hyphens: auto` im Stylesheet). Ohne sie gilt die Sprache der
	 * Seite — und „Geschäftsbericht" bräche nach englischen Regeln, also an
	 * einer sinnlosen Stelle.
	 */
	const lang = String(i18n.language || '').startsWith('ger') ? 'de' : 'en'

	// Eine Junior Mini Company hat eigene Schritte (siehe steps.js), gezeichnet
	// wird sie wie die anderen drei Formen.
	const { phases, steps, lane, currentId, done, total, percent } = props.miniCompany
		? buildMiniCompanyProgress({ miniCompany: props.miniCompany, t })
		: buildCompanyProgress({
				companyType: props.companyType,
				activationsStatus: props.activationsStatus,
				isActivated: props.isActivated,
				isNeedReview: props.isNeedReview,
				reportsDeadline: props.reportsDeadline || props.options?.reportsDeadline,
				t
		  })

	const heute = new Date()
	const currentIndex = steps.findIndex(step => step.id === currentId)
	const laneCurrent = !!lane && lane.step.id === currentId
	const currentStep = laneCurrent ? lane.step : currentIndex === -1 ? null : steps[currentIndex]

	/*
	 * Der Balken läuft vom ersten zum letzten Knoten, nicht von Rand zu Rand:
	 * Ein Stück Schiene hinter dem letzten Schritt sähe aus, als käme noch
	 * etwas. Die Knoten sitzen mittig in gleich breiten Zellen — der halbe
	 * Zellenabstand links und rechts ist also genau der Überstand.
	 */
	const rand = 50 / steps.length

	/*
	 * Gefüllt ist der Balken bis zum laufenden Knoten — dort steht die Company
	 * gerade. Ist alles erledigt, läuft er durch.
	 */
	const fill = laneCurrent
		? // Ist die Abgabe des Konzepts dran, steht die Company an der Stelle, an
		  // der die Spur in den Balken mündet — zwischen zwei Knoten.
		  ((lane.endIndex - 0.5) / (steps.length - 1)) * 100
		: currentIndex === -1
		? 100
		: (currentIndex / (steps.length - 1)) * 100

	// In welchem Abschnitt die Company gerade steht (Überschrift und Fläche
	// türkis). Die Spur gehört zu dem Abschnitt, in dem ihre Abgabe liegt.
	const phaseIsCurrent = phase =>
		phase.steps.some(step => step.id === currentId) || (laneCurrent && lane.phaseId === phase.id)

	const zustand = step => {
		if (step.done) return 'done'
		if (step.id === currentId) return 'current'
		return 'open'
	}

	/*
	 * Der hervorgehobene Knoten muss auch zu sehen sein: Auf schmalen
	 * Bildschirmen wird der Balken geschoben (siehe `.scroller`), und ein Klick
	 * in der Checkliste kann einen Knoten meinen, der gerade rechts aus dem Bild
	 * ragt. `block: 'nearest'` schiebt dabei nur, was nötig ist — sonst spränge
	 * bei jedem Klick auch die Seite.
	 */
	const knoten = useRef({})

	useEffect(() => {
		if (!props.focusId) return

		knoten.current[props.focusId]?.scrollIntoView({ block: 'nearest', inline: 'center' })
	}, [props.focusId])

	return (
		<section
			lang={lang}
			className={clsx(
				styles.root,
				props.compact && styles.compact,
				props.isLoading && styles.loading,
				props.className
			)}
			aria-label={t('Company progress')}
			aria-busy={props.isLoading || undefined}
		>
			<div className={styles.summary}>
				<p className={styles.summaryNext}>
					{currentStep ? (
						<>
							{t('Current step')}:{' '}
							<strong>
								{currentStep.title}
								{laneCurrent && ` – ${t('Skills4Retail submission')}`}
							</strong>
						</>
					) : (
						t('All steps done')
					)}
				</p>
				<p className={styles.summaryCount}>
					{done} {t('of')} {total} {t('done')} · {percent} %
				</p>
			</div>

			<div className={styles.scroller}>
				{/*
				 * Wie breit der Balken mindestens sein muss, hängt an der Zahl der
				 * Schritte: Eine Junior Company hat dreizehn, eine Junior Basic
				 * Company zehn. Eine feste Mindestbreite hieße für die eine
				 * gequetschte Beschriftungen und für die andere unnötiges
				 * Schieben.
				 */}
				<div className={styles.chart} style={{ '--steps': steps.length }}>
					{/*
					 * Die Farbflächen der Abschnitte liegen hinter Überschriften und
					 * Balken, nicht um sie herum: So bleiben Überschrift, Fläche und
					 * Knoten in einer Spalte, ohne dass drei Stellen dieselbe Breite
					 * ausrechnen.
					 */}
					<div className={styles.bands} aria-hidden="true">
						{phases.map(phase => (
							<span
								key={phase.id}
								className={clsx(styles.band, phaseIsCurrent(phase) && styles.bandCurrent)}
								style={{ flexGrow: phase.steps.length }}
							/>
						))}
					</div>

					<div className={styles.phases}>
						{phases.map(phase => (
							<h3
								key={phase.id}
								className={clsx(styles.phase, phaseIsCurrent(phase) && styles.phaseCurrent)}
								style={{ flexGrow: phase.steps.length }}
							>
								{phase.title}
							</h3>
						))}
					</div>

					{lane && (
						<Skills4RetailLane
							lane={lane}
							state={zustand(lane.step)}
							columns={steps.length}
							isActivated={props.isActivated}
							focusId={props.focusId}
							onFocusStep={props.onFocusStep}
							nodeRef={element => {
								knoten.current[lane.step.id] = element
							}}
						/>
					)}

					<ol className={styles.track}>
						<span
							className={styles.rail}
							style={{ left: `${rand}%`, right: `${rand}%` }}
							aria-hidden="true"
						>
							<span className={styles.railFill} style={{ width: `${fill}%` }} />
						</span>

						{steps.map(step => {
							const state = zustand(step)
							const Icon = ICONS[step.icon]
							const ueberfaellig =
								!step.done && step.deadline && differenceInCalendarDays(heute, step.deadline) > 0

							// Anklickbar ist nur, was in der Checkliste auch vorkommt.
							const verknuepft =
								!!props.onFocusStep && hasChecklistCounterpart(step.id, props.isActivated)
							const hervorgehoben = verknuepft && props.focusId === step.id

							// Ohne Checkliste bleibt es bei einem <div>: Ein Knopf, der
							// nichts tut, verspricht etwas, das es nicht gibt.
							const Element = verknuepft ? 'button' : 'div'

							return (
								<li
									key={step.id}
									className={clsx(
										styles.step,
										styles[state],
										step.optional && styles.optional,
										hervorgehoben && styles.focused
									)}
									aria-current={state === 'current' ? 'step' : undefined}
								>
									<Element
										ref={element => {
											knoten.current[step.id] = element
										}}
										type={verknuepft ? 'button' : undefined}
										className={clsx(styles.trigger, verknuepft && styles.triggerLinked)}
										title={verknuepft ? t('Show the matching item in the checklist') : step.title}
										aria-pressed={verknuepft ? hervorgehoben : undefined}
										onClick={verknuepft ? () => props.onFocusStep(step.id) : undefined}
									>
										<span className={styles.node}>
											{step.done ? (
												<IconCheck className={styles.icon} />
											) : (
												<Icon className={styles.icon} />
											)}
										</span>

										<span className={styles.label}>
											{step.title}
											{step.optional && (
												<span className={styles.optionalTag}>{t('optional')}</span>
											)}
											{!!step.deadline && (
												<span
													className={clsx(
														styles.deadline,
														ueberfaellig && styles.deadlineOverdue
													)}
												>
													{t('Deadline')}: {fmt(step.deadline)}
												</span>
											)}
										</span>
									</Element>
								</li>
							)
						})}
					</ol>
				</div>
			</div>
		</section>
	)
}

/*
 * Das Skills4Retail-Konzept als zweite Spur über dem Balken (Anforderung #209).
 *
 * Als Knoten zwischen „Anteile" und „Halbjahresbericht" las es sich, als
 * fange die Arbeit daran erst in der Geschäftstätigkeit an. Tatsächlich lässt
 * es sich ab der Markt- und Risikoanalyse jederzeit bearbeiten; fest steht nur
 * die Abgabe. Die Spur zweigt deshalb am Knoten der Analyse ab, läuft parallel
 * zu den übrigen Schritten und mündet an der Stelle in den Balken, an der das
 * Konzept vorliegen muss — dort sitzt ihr Endpunkt „Abgabe".
 *
 * Sie liegt im selben Kasten wie der Balken (`.chart`) und wird deshalb beim
 * Schieben auf schmalen Bildschirmen mitbewegt. Ihre Lage kommt als Spalten
 * des Balkens: Mitte der Zelle, an der sie beginnt, linker Rand der Zelle, vor
 * der sie endet.
 */
function Skills4RetailLane({ lane, state, columns, isActivated, focusId, onFocusStep, nodeRef }) {
	const { t } = useTranslation()

	const start = ((lane.startIndex + 0.5) / columns) * 100
	const end = (lane.endIndex / columns) * 100

	const { step, deadline } = lane
	const ueberfaellig = !step.done && deadline && differenceInCalendarDays(new Date(), deadline) > 0

	const verknuepft = !!onFocusStep && hasChecklistCounterpart(step.id, isActivated)
	const hervorgehoben = verknuepft && focusId === step.id
	const Element = verknuepft ? 'button' : 'div'

	// Was am Endpunkt steht: erledigt, ein Datum, oder — ohne Frist in den
	// Einstellungen — der Schritt, vor dem das Konzept vorliegen muss.
	const abgabe = step.done
		? t('Skills4Retail submitted')
		: deadline
		? t('Skills4Retail due by {{date}}', { date: fmt(deadline) })
		: lane.dueBeforeId === 'review'
		? t('Skills4Retail due before the review')
		: t('Skills4Retail due before the first report')

	return (
		<div
			className={clsx(styles.lane, styles[`lane_${state}`], hervorgehoben && styles.laneFocused)}
			style={{ '--lane-start': `${start}%`, '--lane-end': `${100 - end}%` }}
			data-testid="s4r-lane"
		>
			<p className={styles.laneTitle}>{t('Skills4Retail – can be worked on at any time')}</p>

			<div className={styles.laneLine}>
				<span className={styles.laneBranch} aria-hidden="true" />
				<span className={styles.laneMerge} aria-hidden="true" />

				<Element
					ref={nodeRef}
					type={verknuepft ? 'button' : undefined}
					className={clsx(styles.laneEnd, verknuepft && styles.triggerLinked)}
					title={verknuepft ? t('Show the matching item in the checklist') : step.title}
					aria-pressed={verknuepft ? hervorgehoben : undefined}
					aria-current={state === 'current' ? 'step' : undefined}
					onClick={verknuepft ? () => onFocusStep(step.id) : undefined}
				>
					<span className={styles.laneNode}>
						{step.done ? <IconCheck className={styles.icon} /> : <IconDocument className={styles.icon} />}
					</span>
					<span className={clsx(styles.laneDue, ueberfaellig && styles.laneOverdue)}>
						<span className={styles.laneDueTitle}>{t('Skills4Retail submission')}</span>
						{abgabe}
					</span>
				</Element>
			</div>
		</div>
	)
}

Skills4RetailLane.propTypes = {
	lane: PropTypes.object.isRequired,
	state: PropTypes.oneOf(['done', 'current', 'open']).isRequired,
	columns: PropTypes.number.isRequired,
	isActivated: PropTypes.bool,
	focusId: PropTypes.string,
	onFocusStep: PropTypes.func,
	nodeRef: PropTypes.func
}

CompanyProgress.propTypes = {
	companyType: PropTypes.string,
	// Statt companyType/activationsStatus: eine Junior Mini Company, wie sie
	// GET …/mini-companies/:id liefert.
	miniCompany: PropTypes.object,
	activationsStatus: PropTypes.object,
	isActivated: PropTypes.bool,
	isNeedReview: PropTypes.bool,
	// Fristen: normalerweise aus den Einstellungen (Redux), zur Not von außen.
	reportsDeadline: PropTypes.object,
	options: PropTypes.object,
	// Schmalere Fassung für das Firmenprofil der Verwaltung.
	compact: PropTypes.bool,
	// Verknüpfung mit der Checkliste darunter (siehe shared/companyStepLink):
	// welcher Schritt gerade hervorgehoben ist und was ein Klick meldet. Fehlen
	// sie, ist der Balken reine Anzeige.
	focusId: PropTypes.string,
	onFocusStep: PropTypes.func,
	// Der Stand wird gerade geholt: Solange steht hier der Anfangszustand, und
	// der ist nicht der der Company. Zurückgenommen dargestellt, statt kurz
	// etwas Falsches zu behaupten.
	isLoading: PropTypes.bool,
	className: PropTypes.string
}

const mapStateToProps = state => ({
	options: state.options
})

export default connect(mapStateToProps)(CompanyProgress)
